import { Middleware } from "openapi-fetch";

const API_SEGMENT = "/api/";

/**
 * Rewrites a path by swapping its "/api/" route segment for the value of VITE_API_PREFIX.
 *
 * The api path constants are authored with a leading "/api/...", and the deployed environment controls where
 * the API actually lives via VITE_API_PREFIX (e.g. "/api" locally, "/" in environments where the region base
 * URL already carries the api prefix). If VITE_API_PREFIX is unset/empty, or the path has no "/api/" segment,
 * the path is returned unchanged.
 *
 * This matches the FIRST "/api/" segment, which may sit after a region base path. That is required because the
 * openapi-fetch client hands this function the full request pathname, e.g. "/api-use/api/engagement/..." when
 * the region base URL is ".../api-use". (Assumes a region base path never itself contains a literal "/api/"
 * segment; the region prefixes in use are hyphenated like "/api-use", "/api-uks", so this holds.)
 *
 * Examples (path -> result):
 *   VITE_API_PREFIX="/api": "/api/engagement/x"         -> "/api/engagement/x"      (no-op)
 *   VITE_API_PREFIX="/":    "/api/engagement/x"         -> "/engagement/x"
 *   VITE_API_PREFIX="/":    "/api-use/api/engagement/x" -> "/api-use/engagement/x"
 */
export function applyApiPrefix(path: string): string {
    const apiPrefix = import.meta.env.VITE_API_PREFIX;

    const index = path.indexOf(API_SEGMENT);
    // Bail when unset/empty (an empty prefix would fuse the surrounding segments together), or when there is
    // no "/api/" segment to swap.
    if (!apiPrefix || index === -1) {
        return path;
    }

    const before = path.slice(0, index);
    // Keep the leading slash of the next segment by only consuming "/api" (not the trailing slash) here.
    const after = path.slice(index + "/api".length);
    // Trim a trailing slash on the prefix so a "/" prefix does not produce a leading "//".
    return before + apiPrefix.replace(/\/$/, "") + after;
}

/**
 * openapi-fetch middleware that applies {@link applyApiPrefix} to the outgoing request URL path.
 *
 * NOTE: this only covers requests made through the openapi-fetch client. The file upload/download helpers in
 * upload-utils.ts build their URLs directly (via XMLHttpRequest) and therefore call applyApiPrefix themselves.
 */
export const apiPrefixMiddleware: Middleware = {
    async onRequest({ request }) {
        const url = new URL(request.url);
        const rewrittenPath = applyApiPrefix(url.pathname);

        if (rewrittenPath === url.pathname) {
            return request;
        }

        url.pathname = rewrittenPath;
        return new Request(url, request);
    },
};
